// components/layout/Sidebar.js
import React from 'react';
import { useTranslations } from '../../hooks/useTranslations';
import { useAuth } from '../../context/AuthContext';
import { useBusinessConfig } from '../../context/BusinessConfigContext';
import { Calendar, CalendarDays, BarChart3, Users, Star, UserCheck, FileText, Zap, Megaphone, Shield, LogOut, MessageSquare, Mail } from 'lucide-react';

export function Sidebar({ currentPage, onNavigate }) {
  const t = useTranslations();
  const { logout, user } = useAuth();
  const { config, logoUrl, logoLoading, isAdmin } = useBusinessConfig();

  // Check if user is admin
  const isCampaignsEnabled = config?.settings?.campaigns_enabled === true;
  const isDoctorsJournalEnabled = config?.settings?.doctorsJournalEnabled === true;

  const menuItems = [
    {
      id: 'dashboard',
      label: 'ניהול תורים',
      icon: <Calendar className="w-5 h-5" />
    },
    {
      id: 'schedule',
      label: t.schedule,
      icon: <CalendarDays className="w-5 h-5" />
    },
    {
      id: 'customers',
      label: t.customers,
      icon: <Users className="w-5 h-5" />,
    },
    ...(isDoctorsJournalEnabled ? [{
      id: 'journal',
      label: 'יומן רופא',
      icon: <FileText className="w-5 h-5" />
    }] : []),
    {
      id: 'services',
      label: t.services,
      icon: <Star className="w-5 h-5" />
    },
    {
      id: 'crew-members',
      label: t.crewMembers,
      icon: <UserCheck className="w-5 h-5" />
    },
    {
      id: 'invoices',
      label: 'ניהול חשבונות',
      icon: <FileText className="w-5 h-5" />
    },
    {
      id: 'insights',
      label: t.insights,
      icon: <BarChart3 className="w-5 h-5" />
    },
    {
      id: 'automations',
      label: t.automations,
      icon: <Zap className="w-5 h-5" />
    },
    ...(isCampaignsEnabled ? [{
      id: 'campaigns',
      label: t.campaigns,
      icon: <Megaphone className="w-5 h-5" />
    }] : []),
    // Admin menu items - only show for admin users
    ...(isAdmin ? [{
      id: 'admin',
      label: 'ניהול מנהל',
      icon: <Shield className="w-5 h-5" />
    }, {
      id: 'templates-admin',
      label: 'ניהול תבניות',
      icon: <MessageSquare className="w-5 h-5" />
    }, {
      id: 'incoming-messages',
      label: 'הודעות נכנסות',
      icon: <Mail className="w-5 h-5" />
    }] : [])
  ];

  return (
    <div className="flex flex-col w-64 border-r border-gray-200 h-full" style={{ backgroundColor: '#FAFAFA' }}>
      {/* Logo/Brand */}
      <div className="flex items-center px-4 py-2 lg:px-6 lg:py-4 border-b border-gray-200">
        <div className="flex items-center">
          {logoLoading ? (
            <div className="h-8 lg:h-12 w-8 lg:w-12 bg-gray-200 animate-pulse rounded" />
          ) : (
            <div className="h-8 lg:h-12 w-auto"></div>
            // <img
            //   src={logoUrl}
            //   alt="Company Logo"
            //   className="h-8 lg:h-12 w-auto"
            //   onError={(e) => {
            //     e.target.src = '/clinic-q-logo.png'; // Fallback if Firebase image fails
            //   }}
            // />
          )}
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
        {menuItems.map((item) => {
          const isActive = currentPage === item.id;
          const baseClasses = "flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200";
          const activeClasses = isActive
            ? "bg-pink-50 text-pink-700 border-r-2 border-pink-500"
            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900";

          return (
            <button
              key={item.id}
              onClick={() => {
                if (typeof onNavigate === 'function') {
                  onNavigate(item.id);
                } else {
                  console.error('onNavigate is not a function:', onNavigate);
                }
              }}
              className={`${baseClasses} ${activeClasses} w-full text-left`}
            >
              <span className={isActive ? "text-pink-500" : "text-gray-400"}>
                {item.icon}
              </span>
              <span className="mr-3">{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* User Info & Logout */}
      <div className="flex-shrink-0 px-4 py-4 border-t border-gray-200 space-y-3">
        {/* User Info */}
        <div className="flex items-center px-3 py-2 bg-gray-50 rounded-lg">
          <div className="w-8 h-8 ml-1 bg-pink-500 rounded-full flex items-center justify-center mr-3">
            <span className="text-white text-sm font-semibold">
              {user?.displayName?.[0] || user?.email?.[0] || 'U'}
            </span>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.displayName || user?.email?.split('@')[0] || 'משתמש'}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {user?.email}
            </p>
            {isAdmin && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 mt-1">
                מנהל
              </span>
            )}
          </div>
        </div>

        {/* Logout Button */}
        <button
          onClick={logout}
          className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors duration-200"
        >
          <LogOut className="w-5 h-5 ml-1" />
          יציאה
        </button>
      </div>
    </div>
  );
}